import {
  FileUploadJob,
  FileUploadJobWithGroupName,
  FileWithGroupName,
  ImageFile,
  SharePointFileLink,
  SharePointFileLinkReference,
  SharePointVideoLink,
  SharePointVideoLinkReference,
  SharePointWebLink,
  SharePointWebLinkReference,
  VideoFileWithGroupName,
  WebImage,
  WebImageReference,
  WebLink,
  WebVideo,
  WebVideoReference,
} from '../../state/types';

export enum PublisherAttachmentType {
  File = 'File',
  FileUploadJob = 'FileUploadJob',
  ImageFile = 'ImageFile',
  ResolvingWebLink = 'ResolvingWebLink',
  SharePointFileLink = 'SharePointFileLink',
  SharePointVideoLink = 'SharePointVideoLink',
  SharePointWebLink = 'SharePointWebLink',
  VideoFile = 'VideoFile',
  WebImage = 'WebImage',
  WebLink = 'WebLink',
  WebVideo = 'WebVideo',
}

type PublisherAttachmentSchema =
  | 'files'
  | 'fileUploadJobs'
  | 'imageFiles'
  | 'resolvingWebLinks'
  | 'sharePointFileLinks'
  | 'sharePointVideoLinks'
  | 'sharePointWebLinks'
  | 'videoFiles'
  | 'webImages'
  | 'webLinks'
  | 'webVideos';

export const attachmentTypeToSchemaMap: Record<PublisherAttachmentType, PublisherAttachmentSchema> = {
  [PublisherAttachmentType.File]: 'files',
  [PublisherAttachmentType.FileUploadJob]: 'fileUploadJobs',
  [PublisherAttachmentType.ImageFile]: 'imageFiles',
  [PublisherAttachmentType.ResolvingWebLink]: 'resolvingWebLinks',
  [PublisherAttachmentType.SharePointFileLink]: 'sharePointFileLinks',
  [PublisherAttachmentType.SharePointVideoLink]: 'sharePointVideoLinks',
  [PublisherAttachmentType.SharePointWebLink]: 'sharePointWebLinks',
  [PublisherAttachmentType.VideoFile]: 'videoFiles',
  [PublisherAttachmentType.WebLink]: 'webLinks',
  [PublisherAttachmentType.WebImage]: 'webImages',
  [PublisherAttachmentType.WebVideo]: 'webVideos',
};

export interface PublisherAttachmentWithType<I, T extends PublisherAttachmentType> {
  readonly item: I;
  readonly type: T;
}

export type PublisherImageFile = PublisherAttachmentWithType<ImageFile, PublisherAttachmentType.ImageFile>;
export type PublisherWebImage = PublisherAttachmentWithType<WebImage, PublisherAttachmentType.WebImage>;
export type PublisherUploadingImage = PublisherAttachmentWithType<FileUploadJob, PublisherAttachmentType.FileUploadJob>;
export type PublisherWebLink = PublisherAttachmentWithType<WebLink, PublisherAttachmentType.WebLink>;
export type PublisherFile = PublisherAttachmentWithType<FileWithGroupName, PublisherAttachmentType.File>;
export type PublisherUploadingFile = PublisherAttachmentWithType<
  FileUploadJobWithGroupName,
  PublisherAttachmentType.FileUploadJob
>;
export type PublisherVideoFile = PublisherAttachmentWithType<VideoFileWithGroupName, PublisherAttachmentType.VideoFile>;
export type PublisherUploadingVideoFile = PublisherAttachmentWithType<
  FileUploadJobWithGroupName,
  PublisherAttachmentType.FileUploadJob
>;
export type PublisherWebVideo = PublisherAttachmentWithType<WebVideo, PublisherAttachmentType.WebVideo>;
export type PublisherSharePointFileLink = PublisherAttachmentWithType<
  SharePointFileLink,
  PublisherAttachmentType.SharePointFileLink
>;
export type PublisherSharePointVideoLink = PublisherAttachmentWithType<
  SharePointVideoLink,
  PublisherAttachmentType.SharePointVideoLink
>;
export type PublisherSharePointWebLink = PublisherAttachmentWithType<
  SharePointWebLink,
  PublisherAttachmentType.SharePointWebLink
>;

export interface LockablePublisherWebImage extends PublisherWebImage, Pick<WebImageReference, 'isLocked'> {}
export interface LockablePublisherWebVideo extends PublisherWebVideo, Pick<WebVideoReference, 'isLocked'> {}
export interface LockablePublisherSharePointFileLink
  extends PublisherSharePointFileLink,
    Pick<SharePointFileLinkReference, 'isLocked'> {}
export interface LockablePublisherSharePointVideoLink
  extends PublisherSharePointVideoLink,
    Pick<SharePointVideoLinkReference, 'isLocked'> {}
export interface LockablePublisherSharePointWebLink
  extends SharePointWebLink,
    Pick<SharePointWebLinkReference, 'isLocked'> {}
export type PublisherImageAttachment = PublisherImageFile | LockablePublisherWebImage | PublisherUploadingImage;
export type PublisherFileAttachment = PublisherFile | PublisherUploadingFile | LockablePublisherSharePointFileLink;
export type PublisherVideoAttachment =
  | PublisherVideoFile
  | PublisherSharePointVideoLink
  | PublisherUploadingVideoFile
  | LockablePublisherWebVideo;
export type PublisherWebLinkAttachment = PublisherWebLink;
export type PublisherSharePointWebLinkAttachment = PublisherSharePointWebLink;

export type PublisherAttachment =
  | PublisherFileAttachment
  | PublisherVideoAttachment
  | PublisherImageAttachment
  | PublisherWebLinkAttachment
  | PublisherSharePointWebLinkAttachment;

const typeGuardCreator =
  <AssertedType extends PublisherAttachment>(type: PublisherAttachmentType) =>
  (attachment: PublisherAttachment): attachment is AssertedType =>
    attachment.type === type;

export const isPublisherImageFile = typeGuardCreator<PublisherImageFile>(PublisherAttachmentType.ImageFile);
export const isPublisherSharePointFileLink = typeGuardCreator<LockablePublisherSharePointFileLink>(
  PublisherAttachmentType.SharePointFileLink
);
export const isPublisherUploadingFile = typeGuardCreator<PublisherUploadingFile>(PublisherAttachmentType.FileUploadJob);
export const isPublisherUploadingVideoFile = typeGuardCreator<PublisherUploadingVideoFile>(
  PublisherAttachmentType.FileUploadJob
);
export const isPublisherVideoFile = typeGuardCreator<PublisherVideoFile>(PublisherAttachmentType.VideoFile);
export const isPublisherWebImage = typeGuardCreator<LockablePublisherWebImage>(PublisherAttachmentType.WebImage);
export const isPublisherWebLink = typeGuardCreator<PublisherWebLink>(PublisherAttachmentType.WebLink);
export const isPublisherWebVideo = typeGuardCreator<LockablePublisherWebVideo>(PublisherAttachmentType.WebVideo);
export const isPublisherSharePointVideoLink = typeGuardCreator<PublisherSharePointVideoLink>(
  PublisherAttachmentType.SharePointVideoLink
);
export const isPublisherSharePointWebLink = typeGuardCreator<PublisherSharePointWebLink>(
  PublisherAttachmentType.SharePointWebLink
);
